/**
 * Language Switcher Component
 *
 * Dropdown menu to switch between the supported languages defined in
 * `src/i18n/supported.js`. Uses i18next for language management and
 * persists preference to localStorage.
 */
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { LanguageIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from '../i18n/supported'

// Short badge ("flag") shown in the menu button. Keep separate from the
// language label — the label is full text, the badge is 2-5 chars max.
const LANGUAGE_BADGES = {
  en: 'EN',
  de: 'DE',
  'pt-BR': 'PT-BR',
}

const languages = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  name: LANGUAGE_LABELS[code] ?? code,
  flag: LANGUAGE_BADGES[code] ?? code.toUpperCase(),
}))

export default function LanguageSwitcher() {
  const { i18n } = useTranslation()

  // Match the current language against supported codes. Match exact first
  // (for region-tagged codes like "pt-BR"), then fall back to prefix match.
  const rawLang = i18n.language || ''
  const currentLanguage =
    languages.find((l) => l.code === rawLang)?.code ||
    languages.find((l) => rawLang.startsWith(l.code))?.code ||
    languages.find((l) => l.code.startsWith(rawLang.split('-')[0]))?.code ||
    languages[0].code

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode)
  }

  const currentLangInfo =
    languages.find((l) => l.code === currentLanguage) || languages[0]

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors">
        <LanguageIcon className="h-5 w-5" />
        <span className="font-medium">{currentLangInfo.flag}</span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-surface-container shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            {languages.map((language) => (
              <Menu.Item key={language.code}>
                {({ active }) => (
                  <button
                    onClick={() => handleLanguageChange(language.code)}
                    className={clsx(
                      'flex w-full items-center justify-between px-4 py-2 text-sm',
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                      currentLanguage === language.code && 'font-semibold'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500">{language.flag}</span>
                      <span>{language.name}</span>
                    </span>
                    {currentLanguage === language.code && (
                      <CheckIcon className="h-4 w-4 text-primary-600" />
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
